import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Avatar,
  Alert,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Shield,
  Vaccines,
  BugReport,
  MedicalServices,
  Restaurant,
  ShoppingCart,
  Schedule,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import SectionHeader from '@shared/SectionHeader';
import FilterBar, { FilterOption } from '@shared/FilterBar';
import PaginatedList from '@shared/PaginatedList';
import EmptyState from '@shared/EmptyState';
import RecommendationCard from '@shared/RecommendationCard';
import recommendationsService, { Recommendation } from '@services/recommendationsService';
import { animalsService } from '@services/animalsService';
// import AnimalSwitcher from '../components/recommendations/AnimalSwitcher';

const RecommendationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnimalId, setSelectedAnimalId] = useState<string | null>(null);
  
  // Paginare
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 12;

  // Filtre
  const [filters, setFilters] = useState({
    animalId: '',
    type: '',
    severity: '',
    search: '',
    resolved: undefined as boolean | undefined
  });

  const filterOptions: FilterOption[] = [
    {
      key: 'animalId',
      label: 'Animal',
      type: 'select',
      options: [
        { value: '', label: 'Toate animalele' },
        { value: 'animal1', label: 'Rex' },
        { value: 'animal2', label: 'Whiskers' }
      ]
    },
    {
      key: 'type',
      label: 'Tip',
      type: 'select',
      options: [
        { value: '', label: 'Toate tipurile' },
        { value: 'vaccin', label: 'Vaccin' },
        { value: 'deparazitare', label: 'Deparazitare' },
        { value: 'control', label: 'Control' },
        { value: 'nutritie', label: 'Nutriție' },
        { value: 'produs', label: 'Produs' }
      ]
    },
    {
      key: 'severity',
      label: 'Severitate',
      type: 'select',
      options: [
        { value: '', label: 'Toate' },
        { value: 'urgent', label: 'Urgent' },
        { value: 'important', label: 'Important' },
        { value: 'info', label: 'Informativ' }
      ]
    },
    {
      key: 'search',
      label: 'Caută',
      type: 'text',
      placeholder: 'Caută în recomandări...'
    }
  ];

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading recommendations...');
      
      // Obține animalele utilizatorului pentru a popula numele
      const animalsResponse = await animalsService.getAnimals();
      const animalsMap = new Map(animalsResponse.map((a: any) => [a.id, a.name]));
      
      // Filtre pentru API
      const apiFilters: any = {
        page: currentPage,
        limit: itemsPerPage
      };

      if (filters.animalId) apiFilters.animalId = filters.animalId;
      if (filters.type) apiFilters.type = filters.type;
      if (filters.severity) apiFilters.severity = filters.severity;
      if (filters.search) {
        // Căutarea se face pe server, dar pentru moment folosim parametrul search
        // TODO: Adaugă suport pentru search în backend
      }
      if (filters.resolved !== undefined) {
        apiFilters.resolved = filters.resolved;
      }

      const result = await recommendationsService.getRecommendations(apiFilters);
      
      // Adaugă numele animalelor la recomandări
      const recommendationsWithAnimalNames = result.data.map((rec: Recommendation) => ({
        ...rec,
        animalName: animalsMap.get(rec.animal_id) || 'Animal necunoscut',
        dueAt: rec.due_at,
        resolvedAt: rec.resolved_at
      }));

      setRecommendations(recommendationsWithAnimalNames);
      setTotalItems(result.total);
      setTotalPages(result.pagination.totalPages || Math.ceil(result.total / itemsPerPage));
      
      console.log('✅ Recommendations loaded:', recommendationsWithAnimalNames.length);
    } catch (err: any) {
      console.error('❌ Error loading recommendations:', err);
      setError(err.message || 'Eroare la încărcarea recomandărilor');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setLoading(true);
      await recommendationsService.recalculateRecommendations(filters.animalId);
      await loadRecommendations();
    } catch (err: any) {
      console.error('Error recalculating:', err);
      setError(err.message || 'Eroare la recalcularea recomandărilor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecommendations();
  }, [filters, currentPage]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setFilters({
      animalId: '',
      type: '',
      severity: '',
      search: '',
      resolved: undefined
    });
    setCurrentPage(1);
  };

  const handleMarkAsResolved = async (recommendationId: string) => {
    try {
      await recommendationsService.resolveRecommendation(recommendationId);
      
      // Reîncarcă recomandările pentru a actualiza lista
      await loadRecommendations();
    } catch (err: any) {
      console.error('Error marking as resolved:', err);
      setError(err.message || 'Eroare la marcarea recomandării ca rezolvată');
    }
  };

  const handleCreateAppointment = (recommendationId: string) => {
    const recommendation = recommendations.find(r => r.id === recommendationId);
    if (recommendation) {
      navigate('/appointments', { 
        state: { 
          animalId: recommendation.animal_id,
          reason: recommendation.title 
        } 
      });
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'urgent': return <Warning sx={{ color: 'error.main' }} />;
      case 'important': return <Info sx={{ color: 'warning.main' }} />;
      default: return <CheckCircle sx={{ color: 'info.main' }} />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vaccin': return <Vaccines />;
      case 'deparazitare': return <BugReport />;
      case 'control': return <MedicalServices />;
      case 'nutritie': return <Restaurant />;
      case 'produs': return <ShoppingCart />;
      default: return <Shield />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'urgent': return 'error';
      case 'important': return 'warning';
      default: return 'info';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'vaccin': return 'success';
      case 'deparazitare': return 'warning';
      case 'control': return 'info';
      case 'nutritie': return 'secondary';
      case 'produs': return 'primary';
      default: return 'default';
    }
  };

  // Încarcă statisticile din API
  const [stats, setStats] = useState({
    total: 0,
    urgent: 0,
    important: 0,
    resolved: 0
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const statistics = await recommendationsService.getStatistics(filters.animalId);
        setStats({
          total: statistics.total,
          urgent: statistics.urgent,
          important: statistics.important,
          resolved: statistics.resolved
        });
      } catch (err) {
        console.error('Error loading statistics:', err);
      }
    };
    loadStats();
  }, [filters.animalId]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <SectionHeader
        title="Recomandări & Prevenție"
        subtitle="Reguli și sugestii dinamice pentru sănătatea animalelor tale"
        actions={{
          primary: {
            label: 'Recalculează',
            icon: <Shield />,
            onClick: handleRecalculate
          }
        }}
        badge={{
          count: stats.urgent,
          color: 'error'
        }}
        onRefresh={loadRecommendations}
        loading={loading}
      />

      {/* Statistici rapide */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" gutterBottom>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total recomandări
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" gutterBottom>
                {stats.urgent}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Urgente
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" gutterBottom>
                {stats.important}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Importante
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" gutterBottom>
                {stats.resolved}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rezolvate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre */}
      <FilterBar
        filters={filterOptions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
        collapsible
      />

      {/* Lista recomandărilor */}
      <PaginatedList
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        loading={loading}
        error={error || undefined}
        emptyMessage="Nu există recomandări active"
      >
        <Grid container spacing={2}>
          {recommendations
            .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            .map((recommendation) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={recommendation.id}>
                <RecommendationCard
                  recommendation={{
                    ...recommendation,
                    dueAt: recommendation.due_at,
                    resolvedAt: recommendation.resolved_at
                  }}
                  onMarkAsResolved={() => handleMarkAsResolved(recommendation.id)}
                  onCreateAppointment={() => handleCreateAppointment(recommendation.id)}
                  onAddToCart={() => console.log('Add to cart:', recommendation.id)}
                />
              </Grid>
            ))}
        </Grid>
      </PaginatedList>
    </Container>
  );
};

export default RecommendationsPage;
